import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { MultiSelectTags } from '@/components/ui/multi-select-tags';
import { Button } from '@/components/ui/button';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { profileApi } from '@/lib/api/profile';
import { onProfileMutationSuccess } from '@/lib/profile-query';
import { codebookApi } from '@/lib/api/codebook';
import {
  ProfileSkillLabelMaxLength,
  ProfileSkillMaxCount,
  type UpdateProfileDto,
} from '@paladin-monorepo/common';
import { SuccessIndicator } from '@/components/ui/success-indicator';

export function SkillsCard() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [showSuccess, setShowSuccess] = useState(false);
  const hideTimeoutRef = useRef<number | null>(null);
  const [selectedSkillIds, setSelectedSkillIds] = useState<string[]>([]);
  const [skillLabelById, setSkillLabelById] = useState<Record<string, string>>({});

  const { data: profile, isLoading: loadingProfile } = useQuery({
    queryKey: ['profile'],
    queryFn: () => profileApi.getProfile(),
  });

  const { data: catalogOptions = [], isLoading: loadingOptions } = useQuery({
    queryKey: ['codebook', 'skills'],
    queryFn: () => codebookApi.listSkills(),
    staleTime: 60_000,
  });

  useEffect(() => {
    if (!profile) return;
    const skills = profile.skills ?? [];
    setSelectedSkillIds(skills.map((skill) => skill.id));
    setSkillLabelById(Object.fromEntries(skills.map((skill) => [skill.id, skill.name])));
  }, [profile]);

  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        window.clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  const optionLabels = useMemo(() => {
    const base = catalogOptions.map((option) => ({
      id: option.id,
      name: option.name ?? option.id,
    }));
    const custom = selectedSkillIds
      .filter((id) => !base.some((option) => option.id === id))
      .map((id) => ({ id, name: skillLabelById[id] ?? id }));
    return [...base, ...custom];
  }, [catalogOptions, selectedSkillIds, skillLabelById]);

  const mutation = useMutation({
    mutationFn: (dto: UpdateProfileDto) => profileApi.updateProfile(dto),
    onSuccess: (data) => {
      const skills = data.skills ?? [];
      setSelectedSkillIds(skills.map((skill) => skill.id));
      setSkillLabelById(Object.fromEntries(skills.map((skill) => [skill.id, skill.name])));
      onProfileMutationSuccess(queryClient, data);
      queryClient.invalidateQueries({ queryKey: ['codebook', 'skills'] });
      setShowSuccess(true);
      if (hideTimeoutRef.current) {
        window.clearTimeout(hideTimeoutRef.current);
      }
      hideTimeoutRef.current = window.setTimeout(() => {
        setShowSuccess(false);
      }, 3000);
    },
    onError: () => {
      toast.error(t('profile.skills.saveError'));
    },
  });

  const validateSkillIds = useCallback(
    (ids: string[], labels: Record<string, string>): string | null => {
      if (new Set(ids).size !== ids.length) {
        return t('profile.skills.duplicateError');
      }
      if (ids.length > ProfileSkillMaxCount) {
        return t('profile.skills.maxCountError');
      }
      for (const id of ids) {
        const label = labels[id] ?? id;
        if (label.trim().length > ProfileSkillLabelMaxLength) {
          return t('profile.skills.maxLengthError');
        }
      }
      return null;
    },
    [t],
  );

  const onChangeSkills = (nextIds: string[]) => {
    const labels = Object.fromEntries(
      nextIds.map((id) => [id, optionLabels.find((option) => option.id === id)?.name ?? skillLabelById[id] ?? id]),
    );
    const error = validateSkillIds(nextIds, labels);
    if (error) {
      toast.error(error);
      return;
    }
    setSelectedSkillIds(nextIds);
    setSkillLabelById((prev) => ({ ...prev, ...labels }));
  };

  const onCreateOption = (label: string) => {
    const trimmed = label.trim();
    if (!trimmed) return null;
    if (trimmed.length > ProfileSkillLabelMaxLength) {
      toast.error(t('profile.skills.maxLengthError'));
      return null;
    }
    const existing = optionLabels.find(
      (option) =>
        option.id === trimmed || option.name.toLowerCase() === trimmed.toLowerCase(),
    );
    if (existing) {
      if (!selectedSkillIds.includes(existing.id)) {
        onChangeSkills([...selectedSkillIds, existing.id]);
      }
      return null;
    }
    if (selectedSkillIds.length >= ProfileSkillMaxCount) {
      toast.error(t('profile.skills.maxCountError'));
      return null;
    }
    setSkillLabelById((prev) => ({ ...prev, [trimmed]: trimmed }));
    return { id: trimmed, name: trimmed };
  };

  const save = () => {
    const error = validateSkillIds(selectedSkillIds, skillLabelById);
    if (error) {
      toast.error(error);
      return;
    }
    mutation.mutate({ skills: selectedSkillIds });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {t('profile.skills.title')}
          <SuccessIndicator visible={showSuccess} />
        </CardTitle>
        <CardDescription>{t('profile.skills.description')}</CardDescription>
      </CardHeader>
      <CardContent>
        {loadingProfile || loadingOptions ? (
          <div>{t('generic.loading')}</div>
        ) : (
          <div className="space-y-6">
            <MultiSelectTags
              label={t('profile.skills.label')}
              value={selectedSkillIds}
              onChange={onChangeSkills}
              options={optionLabels}
              placeholder={t('profile.skills.placeholder')}
              filterable
              allowCreate
              onCreateOption={onCreateOption}
              createOptionText={(label) => t('profile.skills.createOption', { label })}
              searchPlaceholder={t('profile.multiSelect.searchPlaceholder')}
              noResultsText={t('profile.multiSelect.noResults')}
            />
            <Button type="button" onClick={save} disabled={mutation.isPending}>
              {mutation.isPending ? t('generic.saving') : t('profile.skills.save')}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
